import { useEffect, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '@/config';
import CurrencyService from '@/services/CurrencyService';
import { ccySym, ccyStripeOk } from '@/utils/currencyUtils';
import {
  targetPrice as computeTargetPrice, ownPrice as computeOwnPrice, hasNoPriceToAccept, defaultCurrency,
  negotiationPrice, negotiationDiff, toCurrencyPrecision,
} from '@/utils/propositionPricing';

/**
 * Prix de départ d'une contre-offre dans la devise choisie : le prix de celui qui propose (`ownPrice`),
 * converti au taux du jour si la devise choisie n'est pas celle de son annonce.
 * Chaîne vide tant qu'il n'y a rien à préremplir (pas de prix, conversion en cours ou indisponible).
 */
export function useNegotiationStart(own, currency) {
  const amount = own?.amount ?? null;
  const from = own?.currency ?? null;
  const [start, setStart] = useState('');

  useEffect(() => {
    if (amount == null || !currency) { setStart(''); return undefined; }
    if (currency === from) { setStart(String(amount)); return undefined; }
    let cancelled = false;
    setStart('');
    CurrencyService.convert(amount, from, currency)
      .then((res) => { if (!cancelled) setStart(negotiationPrice(res?.convertedAmount ?? null, currency, from, amount)); })
      .catch(() => { if (!cancelled) setStart(''); });
    return () => { cancelled = true; };
  }, [amount, from, currency]);

  return start;
}

/**
 * Montant `source` ({ amount, currency }) converti au taux du jour dans `currency` ; null quand il n'y a rien à
 * convertir (même devise, pas de montant) ou tant que la conversion manque.
 */
export function useConvertedAmount(source, currency) {
  const amount = source?.amount ?? null;
  const from = source?.currency ?? null;
  const [converted, setConverted] = useState(null);

  useEffect(() => {
    setConverted(null);
    if (amount == null || !currency || currency === from) return undefined;
    let cancelled = false;
    CurrencyService.convert(amount, from, currency)
      .then((res) => { if (!cancelled) setConverted(res?.convertedAmount ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [amount, from, currency]);

  return converted;
}

/**
 * Frais Colis2Ship de l'expéditeur (C8, 2026-10-27) pour un montant : calculés par le serveur, qui connaît le
 * transporteur (plafond de 15 % commission + frais) et l'expéditeur (1re mission offerte). null tant qu'inconnus.
 */
function useSenderFee(enabled, amount, currency, tripId, reserved) {
  const [fee, setFee] = useState(null);
  useEffect(() => {
    setFee(null);
    if (!enabled || !tripId || !(Number(amount) > 0) || !ccyStripeOk(currency)) return undefined;
    let cancelled = false;
    // Attente courte : en négociation, le montant change à chaque frappe
    const timer = setTimeout(() => {
      Promise.resolve(axios.get(`${BACKEND_URL}/api/missions/sender-fee-preview`, {
        params: { amount: Number(amount), currency, trip_id: tripId, payment_reserved: reserved },
      }))
        .then((r) => { if (!cancelled && r?.data) setFee(r.data); })
        .catch(() => {});
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled, amount, currency, tripId, reserved]);
  return fee;
}

/**
 * Brouillon d'une proposition : la seule source du prix, de la devise, des garde-fous d'envoi et du contenu
 * de la requête, pour les quatre écrans qui en envoient. Règles : `utils/propositionPricing.js`.
 *
 * `target` : l'annonce visée · `mine` : mon annonce choisie (ou null). Le sélecteur de devise de l'écran reçoit
 * `currency` / `setCurrency`, le prix de l'annonce visée (`targetPrice`) comme montant à convertir, et rend
 * le montant converti par `onTargetConverted`.
 *
 * `escrow` : l'écran propose aussi le séquestre (expéditeur sur un trajet). Il réserve le prix du trajet dans la
 * devise choisie : `escrowPrice`, le montant exact que le serveur recalculera (D24).
 */
export function usePropositionDraft({ meSide, target, mine, escrow = false }) {
  const tp = computeTargetPrice(meSide, target, mine);
  const own = computeOwnPrice(meSide, mine, target);
  const targetCurrency = target?.primary_currency || 'EUR';

  const [currency, setCurrency] = useState(null);
  const [targetPriceConverted, setTargetPriceConverted] = useState(null);
  const [price, setPrice] = useState('');

  useEffect(() => {
    if (target) setCurrency(defaultCurrency(target, mine));
  }, [target?.id, mine?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setTargetPriceConverted(null); }, [tp?.amount, currency]);

  const negStart = useNegotiationStart(own, currency);
  useEffect(() => { setPrice(negStart); }, [negStart]);

  const sameCurrency = !currency || currency === targetCurrency;
  // Le prix de l'annonce visée dans la devise de la proposition : tel quel, ou converti (null tant qu'il manque)
  const targetPriceInCurrency = tp == null ? null : (sameCurrency ? tp.amount : targetPriceConverted);
  const directPrice = targetPriceInCurrency != null ? toCurrencyPrecision(targetPriceInCurrency, currency || targetCurrency) : null;

  // Séquestre : le prix du trajet tel quel, ou converti par le service de taux — le même que celui du serveur
  const escrowSource = escrow && meSide === 'sender' ? tp : null;
  const escrowSameCurrency = !currency || currency === escrowSource?.currency;
  const escrowConverted = useConvertedAmount(escrowSource, currency);
  const escrowPrice = escrowSource == null ? null : (escrowSameCurrency ? escrowSource.amount : escrowConverted);

  // Plancher de mission : deux fois la commission minimale du transporteur, dans la devise choisie (D20).
  // Le serveur le calcule (il connaît le transporteur) et l'applique à l'envoi ; ici on prévient avant.
  const tripId = meSide === 'sender' ? target?.id : mine?.id;
  const [floor, setFloor] = useState(null);
  useEffect(() => {
    setFloor(null);
    if (!tripId || !mine || !target || !ccyStripeOk(currency)) return undefined;
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/propositions/price-floor`, { params: { trip_id: tripId, currency } })
      .then((r) => { if (!cancelled && r?.data?.price_floor > 0) setFloor(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tripId, mine?.id, target?.id, currency]); // eslint-disable-line react-hooks/exhaustive-deps

  // Frais de l'expéditeur, un par façon de procéder (le séquestre change la commission du transporteur, donc le plafond)
  const feeOn = meSide === 'sender' && !!mine;
  const feeDirect = useSenderFee(feeOn, directPrice, currency, tripId, false);
  const feeNegotiation = useSenderFee(feeOn, price, currency, tripId, false);
  const feeEscrow = useSenderFee(feeOn && escrow, escrowPrice, currency, tripId, true);
  const senderFee = (mode) => ({ direct: feeDirect, negotiation: feeNegotiation, escrow: feeEscrow }[mode] || null);

  const noPriceToAccept = hasNoPriceToAccept(meSide, target);
  const conversionMissing = tp != null && !sameCurrency && targetPriceConverted == null;
  const priceMissing = !(Number(price) > 0);

  const sym = ccySym(currency || targetCurrency);
  const belowFloor = (mode) => {
    const proposed = mode === 'direct' ? directPrice : Number(price);
    return !!floor && proposed > 0 && proposed < floor.price_floor;
  };
  const floorNote = (mode) => (belowFloor(mode)
    ? `Prix minimum : ${floor.price_floor} ${sym}. La commission minimale du transporteur est de ${floor.commission_min} ${sym} et ne peut pas dépasser la moitié du prix.`
    : null);

  const canSubmit = (mode) => !!mine && ccyStripeOk(currency) && !belowFloor(mode)
    && (mode === 'direct' ? !noPriceToAccept && !conversionMissing : !priceMissing);

  const payload = (mode) => {
    const body = {
      shipment_id: meSide === 'sender' ? mine.id : target.id,
      trip_id: meSide === 'sender' ? target.id : mine.id,
      is_direct: mode === 'direct',
    };
    const proposed = mode === 'direct' ? directPrice : Number(price);
    if (proposed) body.proposed_price = proposed;
    if (currency) body.currency = currency;
    return body;
  };

  return {
    targetPrice: tp,
    ownPrice: own,
    currency,
    setCurrency,
    onTargetConverted: setTargetPriceConverted,
    targetPriceConverted,
    directPrice,
    escrowPrice,
    escrowConversionMissing: escrowSource != null && !escrowSameCurrency && escrowConverted == null,
    price,
    setPrice,
    diff: negotiationDiff(price, directPrice, meSide === 'sender' ? 'tarif' : 'budget', sym),
    priceFloor: floor,
    senderFee,
    belowFloor,
    floorNote,
    noPriceToAccept,
    conversionMissing,
    priceMissing,
    canSubmit,
    payload,
  };
}
